﻿import { useCallback, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Feedback, useResource } from "@/components/workflow/shared";
import { api } from "@/lib/api";
import { E2Errors, useE2Save } from "./E2Shared";
import { Field } from "./shared";

export function EquipmentSettings() {
  const { user } = useAuth();
  const allowed =
    user?.permissions.some(
      (p) => p.module === "auth" && p.action === "update" && p.scope === "all",
    ) ?? false;
  const resource = useResource(
    `e2-settings:${allowed}`,
    useCallback(
      async (signal) =>
        allowed
          ? (
              await api.get<{
                oprema_inventura_gradiliste_dani: number;
                oprema_inventura_baza_dani: number;
              }>("/postavke", { signal })
            ).data
          : null,
      [allowed],
    ),
  );
  const save = useE2Save(),
    [site, setSite] = useState<number | "" | null>(null),
    [base, setBase] = useState<number | "" | null>(null),
    [reason, setReason] = useState(""),
    [message, setMessage] = useState("");
  if (!allowed) return null;
  const siteDays = site ?? resource.data?.oprema_inventura_gradiliste_dani,
    baseDays = base ?? resource.data?.oprema_inventura_baza_dani;
  const valid =
    siteDays !== undefined &&
    baseDays !== undefined &&
    [siteDays, baseDays].every(
      (n) => typeof n === "number" && Number.isInteger(n) && n >= 1 && n <= 3650,
    ) &&
    !!reason.trim();
  return (
    <section className="site-equipment equipment-card">
      <h2 className="text-xl font-bold">Oprema</h2>
      <Feedback error={resource.error} message={message} />
      {resource.loading && <p role="status">Učitavanje postavki opreme…</p>}
      {resource.data && siteDays !== undefined && baseDays !== undefined && (
        <form
          className="equipment-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (!valid) return;
            void save.run(
              async () =>
                api.patch("/postavke", {
                  oprema_inventura_gradiliste_dani: siteDays,
                  oprema_inventura_baza_dani: baseDays,
                  razlog: reason.trim(),
                }),
              () => {
                setMessage("Postavke opreme spremljene.");
                setSite(null);
                setBase(null);
                setReason("");
                resource.reload();
              },
            );
          }}
        >
          <Field
            label="Rok inventure gradilišta (dana)"
            error={save.fields.oprema_inventura_gradiliste_dani}
          >
            <input
              required
              type="number"
              min={1}
              max={3650}
              step={1}
              value={siteDays ?? ""}
              onChange={(e) => setSite(e.target.value === "" ? "" : e.target.valueAsNumber)}
            />
          </Field>
          <Field
            label="Rok inventure Baze (dana)"
            error={save.fields.oprema_inventura_baza_dani}
          >
            <input
              required
              type="number"
              min={1}
              max={3650}
              step={1}
              value={baseDays ?? ""}
              onChange={(e) => setBase(e.target.value === "" ? "" : e.target.valueAsNumber)}
            />
          </Field>
          <Field label="Razlog promjene roka">
            <textarea
              required
              maxLength={2000}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </Field>
          <E2Errors save={save} />
          <Button disabled={save.busy || !valid}>Spremi postavke opreme</Button>
        </form>
      )}
    </section>
  );
}
